import type Konva from 'konva';
import { ChangeNodeStyleCommand } from '../../../core/commands/ChangeNodeStyleCommand';
import { MoveNodeCommand, ScaleNodeCommand, ScaleNodesCommand } from '../../../core/commands/TransformCommands';

const MIN_PDF_NODE_WIDTH = 120;
const MIN_PDF_NODE_HEIGHT = 84;
const LIVE_SIZED_ORG_NODE_TYPES = new Set([
  'org_person_photo',
  'org_person_photo_left',
  'org_person_photo_right',
  'org_person_photo_inner_left',
  'org_person_text',
  'org_position',
  'org_department',
]);

const readLiveNodeSize = (konvaNode: any, newSize: { w: number; h: number }): void => {
  try {
    const widthNow = typeof konvaNode?.width === 'function' ? konvaNode.width() : 0;
    const heightNow = typeof konvaNode?.height === 'function' ? konvaNode.height() : 0;
    if (typeof widthNow === 'number' && widthNow > 0 && !Number.isNaN(widthNow)) {
      newSize.w = Math.max(20, widthNow);
    }
    if (typeof heightNow === 'number' && heightNow > 0 && !Number.isNaN(heightNow)) {
      newSize.h = Math.max(20, heightNow);
    }
  } catch {}
};

const isLiveSizedOrgNodeType = (type: unknown): boolean =>
  typeof type === 'string' && LIVE_SIZED_ORG_NODE_TYPES.has(type);

export function useCanvasTransformHandlers(input: {
  setIsTransforming: (next: boolean) => void;
  checkWritePermission: () => boolean;

  nodes: any[];
  nodeRefs: { current: Map<string, Konva.Node> };
  transformerRef: { current: any };
  transformStartState: { current: Map<string, { x: number; y: number; w: number; h: number }> };
  selectedIds: Set<string>;

  commandManager: any;
  scheduleDirtyForNode: (nodeId: string) => void;
  scheduleDirtyForNodes: (nodeIds: Set<string>) => void;
}): {
  handleTransformStart: (nodeId: string, e: Konva.KonvaEventObject<Event>) => void;
  handleTransform: (nodeId: string, e: Konva.KonvaEventObject<Event>) => void;
  handleTransformEnd: (nodeId: string, e: Konva.KonvaEventObject<Event>) => void;
} {
  const {
    setIsTransforming,
    checkWritePermission,
    nodes,
    nodeRefs,
    transformerRef,
    transformStartState,
    selectedIds,
    commandManager,
    scheduleDirtyForNode,
    scheduleDirtyForNodes,
  } = input;

  // Helper function to scale a freehand shape's points array
  const scaleFreehandShape = (
    id: string,
    nodeData: any,
    konvaNode: any,
    scaleX: number,
    scaleY: number,
    measuredTopLeft: { x: number; y: number },
  ): void => {
    const oldPoints = Array.isArray(nodeData.attrs?.points) ? (nodeData.attrs.points as number[]) : [];
    if (oldPoints.length < 4) return;

    // Calculate bounds of original points (in local coordinates relative to node.x, node.y)
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (let i = 0; i < oldPoints.length; i += 2) {
      const px = oldPoints[i] as number;
      const py = oldPoints[i + 1] as number;
      if (Number.isFinite(px) && Number.isFinite(py)) {
        if (px < minX) minX = px;
        if (py < minY) minY = py;
        if (px > maxX) maxX = px;
        if (py > maxY) maxY = py;
      }
    }

    if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) return;

    const oldWidth = maxX - minX;
    const oldHeight = maxY - minY;
    const transformedWidth = oldWidth * scaleX;
    const transformedHeight = oldHeight * scaleY;
    const newWidth = Math.max(1, transformedWidth);
    const newHeight = Math.max(1, transformedHeight);
    const scaleFactorX = oldWidth > 0 ? newWidth / oldWidth : 1;
    const scaleFactorY = oldHeight > 0 ? newHeight / oldHeight : 1;

    // Scale all points relative to the original min point (keeping local coordinates)
    const scaledPoints: number[] = [];
    for (let i = 0; i < oldPoints.length; i += 2) {
      const px = oldPoints[i] as number;
      const py = oldPoints[i + 1] as number;
      if (Number.isFinite(px) && Number.isFinite(py)) {
        const relativeX = px - minX;
        const relativeY = py - minY;
        scaledPoints.push(minX + relativeX * scaleFactorX, minY + relativeY * scaleFactorY);
      } else {
        scaledPoints.push(px, py);
      }
    }

    // Calculate position adjustment based on bounding box movement
    const originalCanvasLeft = nodeData.x + minX;
    const originalCanvasTop = nodeData.y + minY;
    const deltaX = measuredTopLeft.x - originalCanvasLeft;
    const deltaY = measuredTopLeft.y - originalCanvasTop;
    const correctedPosition = {
      x: nodeData.x + deltaX,
      y: nodeData.y + deltaY,
    };

    // Update points
    const cmd = new ChangeNodeStyleCommand([id], 'points', scaledPoints);
    commandManager.execute(cmd);

    // Update position if needed
    if (Math.abs(correctedPosition.x - nodeData.x) > 0.01 || Math.abs(correctedPosition.y - nodeData.y) > 0.01) {
      const moveCmd = new MoveNodeCommand(id, correctedPosition, { x: nodeData.x, y: nodeData.y });
      commandManager.execute(moveCmd);

      // Update Konva node position immediately to prevent visual jump
      if (konvaNode) {
        try {
          konvaNode.position({ x: correctedPosition.x, y: correctedPosition.y });
          konvaNode.getLayer()?.batchDraw();
        } catch { }
      }
    }
  };

  const handleTransformEnd = (nodeId: string, e: Konva.KonvaEventObject<Event>) => {
    setIsTransforming(false);
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // For freehand shapes, use transformer bounds instead of node bounds
    // IMPORTANT: Get bounds BEFORE resetting scale, otherwise we lose the transform information
    const nodeData = nodes.find((n) => n.id === nodeId);
    let measuredTopLeft = { x: 0, y: 0 };

    if (nodeData?.type === 'freehand' && transformerRef.current) {
      // Use transformer's bounds for freehand shapes (before resetting scale)
      try {
        const tr = transformerRef.current;
        if (tr && tr.nodes().length > 0 && tr.nodes()[0] === node) {
          const trRect = tr.getClientRect();
          const stage = node.getStage();
          const stageScale = stage?.scaleX() || 1;
          const stagePos = stage?.position() || { x: 0, y: 0 };
          measuredTopLeft = {
            x: (trRect.x - stagePos.x) / stageScale,
            y: (trRect.y - stagePos.y) / stageScale,
          };
        }
      } catch { }
    }

    // If we didn't get transformer bounds, fall back to node bounds (before resetting scale)
    if (measuredTopLeft.x === 0 && measuredTopLeft.y === 0) {
      const stage = node.getStage();
      const stageScale = stage?.scaleX() || 1;
      const stagePos = stage?.position() || { x: 0, y: 0 };
      let measureNode: Konva.Node = node;
      const rootKn = nodeRefs.current.get(nodeId) as Konva.Node | undefined;
      if (rootKn && rootKn !== node && (node as any).getParent?.() === rootKn) {
        measureNode = rootKn;
      }
      const rawRect = measureNode.getClientRect({ skipShadow: true });
      measuredTopLeft = {
        x: (rawRect.x - stagePos.x) / stageScale,
        y: (rawRect.y - stagePos.y) / stageScale,
      };
    }

    // Reset scale on the Konva node AFTER capturing all needed information
    node.scaleX(1);
    node.scaleY(1);

    // Check if multiple nodes are being transformed
    if (selectedIds.size > 1 && transformerRef.current) {
      // Handle multi-selection transform
      const scales: Array<{
        nodeId: string;
        oldSize: { w: number; h: number };
        newSize: { w: number; h: number };
        oldPosition: { x: number; y: number };
        newPosition: { x: number; y: number };
      }> = [];

      // Process freehand shapes separately (they need special handling)
      const freehandIds: string[] = [];
      const regularIds: string[] = [];

      selectedIds.forEach((id) => {
        const nodeData = nodes.find((n) => n.id === id);
        if (nodeData?.type === 'freehand') {
          freehandIds.push(id);
        } else {
          regularIds.push(id);
        }
      });

      // Handle regular shapes
      regularIds.forEach((id) => {
        const oldState = transformStartState.current.get(id);
        const nodeData = nodes.find((n) => n.id === id);
        const konvaNode = nodeRefs.current.get(id);

        if (oldState && nodeData && konvaNode) {
          const isFlowchartNode =
            nodeData.type === 'process' ||
            nodeData.type === 'decision' ||
            nodeData.type === 'terminator' ||
            nodeData.type === 'data' ||
            nodeData.type === 'document' ||
            nodeData.type === 'flowchart-shape';
          const isBasicShapeNode = nodeData.type === 'basic-shape';
          const isLiveSizedOrgNode = isLiveSizedOrgNodeType(nodeData.type);

          // Apply the same scale to all selected nodes
          const newSize = {
            w: Math.max(20, (nodeData.w ?? nodeData.width ?? 60) * scaleX),
            h: Math.max(20, (nodeData.h ?? nodeData.height ?? 60) * scaleY),
          };
          if (nodeData.type === 'pdf') {
            newSize.w = Math.max(MIN_PDF_NODE_WIDTH, newSize.w);
            newSize.h = Math.max(MIN_PDF_NODE_HEIGHT, newSize.h);
          }

          // These renderers keep scale at 1 during drag; use live bounds as source of truth.
          if (isFlowchartNode || isBasicShapeNode || isLiveSizedOrgNode) {
            try {
              if (isBasicShapeNode || isLiveSizedOrgNode) {
                readLiveNodeSize(konvaNode, newSize);
              } else {
                const bounds = (konvaNode as any)?.findOne?.('.text-bounds');
                if (bounds) {
                  const wNow = typeof bounds.width === 'function' ? bounds.width() : bounds.width;
                  const hNow = typeof bounds.height === 'function' ? bounds.height() : bounds.height;
                  if (typeof wNow === 'number' && wNow > 0 && !Number.isNaN(wNow)) {
                    newSize.w = Math.max(20, wNow + 24);
                  }
                  if (typeof hNow === 'number' && hNow > 0 && !Number.isNaN(hNow)) {
                    newSize.h = Math.max(20, hNow + 24);
                  }
                }
              }
            } catch { }
          }

          scales.push({
            nodeId: id,
            oldSize: { w: oldState.w, h: oldState.h },
            newSize,
            oldPosition: { x: oldState.x, y: oldState.y },
            newPosition: { x: (konvaNode as any).x(), y: (konvaNode as any).y() },
          });
        }
      });

      // Handle freehand shapes individually (each needs its own bounds calculation)
      freehandIds.forEach((id) => {
        const oldState = transformStartState.current.get(id);
        const nodeData = nodes.find((n) => n.id === id);
        const konvaNode = nodeRefs.current.get(id) as any;

        if (oldState && nodeData && konvaNode && nodeData.type === 'freehand') {
          // Get individual node bounds BEFORE resetting scale
          let individualMeasuredTopLeft = { x: 0, y: 0 };
          try {
            const rawRect = konvaNode.getClientRect({ skipShadow: true });
            const stage = konvaNode.getStage();
            const stageScale = stage?.scaleX() || 1;
            const stagePos = stage?.position() || { x: 0, y: 0 };
            individualMeasuredTopLeft = {
              x: (rawRect.x - stagePos.x) / stageScale,
              y: (rawRect.y - stagePos.y) / stageScale,
            };
          } catch { }

          // Reset scale on this node
          try {
            konvaNode.scaleX(1);
            konvaNode.scaleY(1);
          } catch { }

          // Use helper function to scale the freehand shape
          scaleFreehandShape(id, nodeData, konvaNode, scaleX, scaleY, individualMeasuredTopLeft);
        }
      });

      // Execute scale command for regular shapes
      if (scales.length > 0) {
        const command = new ScaleNodesCommand(scales);
        commandManager.execute(command);
      }

      // Clear all tracked states
      transformStartState.current.clear();
      // Ensure only relevant connectors recompute after multi-transform
      scheduleDirtyForNodes(selectedIds);
      setTimeout(() => scheduleDirtyForNodes(selectedIds), 0);
      setTimeout(() => scheduleDirtyForNodes(selectedIds), 50);
    } else {
      // Single node transform
      const oldState = transformStartState.current.get(nodeId);
      const nodeData = nodes.find((n) => n.id === nodeId);

      if (nodeData && oldState) {
        const newSize = {
          w: Math.max(20, (nodeData.w ?? nodeData.width ?? 60) * scaleX),
          h: Math.max(20, (nodeData.h ?? nodeData.height ?? 60) * scaleY),
        } as { w: number; h: number };
        if (nodeData.type === 'pdf') {
          newSize.w = Math.max(MIN_PDF_NODE_WIDTH, newSize.w);
          newSize.h = Math.max(MIN_PDF_NODE_HEIGHT, newSize.h);
        }
        const lockAspectRatio = Boolean((nodeData.attrs as any)?.aspectRatioLocked);

        const isFlowchartNode =
          nodeData.type === 'process' ||
          nodeData.type === 'decision' ||
          nodeData.type === 'terminator' ||
          nodeData.type === 'data' ||
          nodeData.type === 'document' ||
          nodeData.type === 'flowchart-shape';
        const isBasicShapeNode = nodeData.type === 'basic-shape';
        const isLiveSizedOrgNode = isLiveSizedOrgNodeType(nodeData.type);
        // For text nodes and sticky notes, clamp to minimum content bounds so box never shrinks below text
        if (nodeData.type === 'text' || nodeData.type === 'sticky-note') {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (ctx) {
              const fontPx = Math.max(1, (nodeData.fontSize as number) || 16);
              const family = (nodeData.fontFamily as string) || 'Noto Sans';
              const fontStyle = `${(nodeData as any)?.attrs?.fontStyle || 'normal'}`;
              const fontWeight = `${(nodeData as any)?.attrs?.fontWeight || 'normal'}`;
              ctx.font = `${fontStyle !== 'normal' ? fontStyle + ' ' : ''}${fontWeight !== 'normal' ? fontWeight + ' ' : ''}${fontPx}px ${family}`;
              const text = String(nodeData.text || 'Text');
              const lines = text.split('\n');
              let maxLine = 0;
              for (const ln of lines) {
                const w = ctx.measureText(ln || ' ').width;
                if (w > maxLine) maxLine = w;
              }
              const minW = Math.ceil(maxLine) + 6; // small padding
              const lineH = fontPx * 1.2;
              const minH = Math.ceil(Math.max(lineH, 14));
              // For sticky notes, add 20px padding (10px on each side)
              const padding = nodeData.type === 'sticky-note' ? 20 : 0;
              newSize.w = Math.max(newSize.w, minW + padding);
              newSize.h = Math.max(newSize.h, minH + padding);
            }
          } catch { }
        }
        // For text nodes and sticky notes, derive final size from live bounds since we kept scale at 1 during drag
        // For flowchart with rotation-group (genogram-style): use scale-based newSize, not text-bounds
        const textKn = nodeRefs.current.get(nodeId) as any;
        const hasFlowchartRotationGroup = isFlowchartNode && !!textKn?.findOne?.('.rotation-group');
        if (nodeData.type === 'text' || nodeData.type === 'sticky-note' || (isFlowchartNode && !hasFlowchartRotationGroup) || isBasicShapeNode || isLiveSizedOrgNode) {
          try {
            if (isBasicShapeNode || isLiveSizedOrgNode) {
              readLiveNodeSize(textKn, newSize);
            } else {
              const bounds = textKn?.findOne?.('.text-bounds');
              if (bounds) {
                const wNow = typeof bounds.width === 'function' ? bounds.width() : bounds.width;
                const hNow = typeof bounds.height === 'function' ? bounds.height() : bounds.height;
                if (typeof wNow === 'number' && wNow > 0 && !Number.isNaN(wNow)) {
                  // Sticky notes: bounds excludes padding; flowchart shapes: bounds excludes 12px padding each side
                  const finalW = nodeData.type === 'sticky-note' ? wNow + 20 : isFlowchartNode ? wNow + 24 : wNow;
                  newSize.w = finalW;
                }
                if (typeof hNow === 'number' && hNow > 0 && !Number.isNaN(hNow)) {
                  const finalH = nodeData.type === 'sticky-note' ? hNow + 20 : isFlowchartNode ? hNow + 24 : hNow;
                  newSize.h = finalH;
                }
              }
            }
          } catch { }
        }

        if (
          (nodeData.type === 'flowchart-shape' || nodeData.type === 'bpmn-shape') &&
          (nodeData.attrs as any)?.variant === 'bpmn-event'
        ) {
          const size = Math.min(newSize.w, newSize.h);
          newSize.w = size;
          newSize.h = size;
        }

        if (lockAspectRatio) {
          const baseW = Math.max(1, oldState.w ?? nodeData.w ?? nodeData.width ?? 1);
          const baseH = Math.max(1, oldState.h ?? nodeData.h ?? nodeData.height ?? 1);
          const ratio = baseW / baseH;
          const widthDelta = Math.abs(newSize.w - baseW);
          const heightDelta = Math.abs(newSize.h - baseH);
          if (widthDelta >= heightDelta) {
            newSize.h = Math.max(20, newSize.w / ratio);
          } else {
            newSize.w = Math.max(20, newSize.h * ratio);
          }
        }

        // Live-sized org renderers update width/height during transform and keep scale at 1.
        // Their rendered client rect can include stroke/avatar/footer overflow, so committing
        // that rect origin causes a small persisted x/y jump after resize.
        const newPosition = isLiveSizedOrgNode
          ? { x: oldState.x, y: oldState.y }
          : { x: measuredTopLeft.x, y: measuredTopLeft.y };

        // Special handling for freehand shapes - scale points array instead of width/height
        if (nodeData.type === 'freehand') {
          const kn = nodeRefs.current.get(nodeId) as any;
          scaleFreehandShape(nodeId, nodeData, kn, scaleX, scaleY, measuredTopLeft);
        } else {
          // Create and execute scale command for non-freehand shapes
          const command = new ScaleNodeCommand(nodeId, newSize, newPosition);
          commandManager.execute(command);
        }

        // Clear the tracked state
        transformStartState.current.delete(nodeId);
      }
      // Ensure only relevant connectors recompute after single transform
      scheduleDirtyForNode(nodeId);
      setTimeout(() => scheduleDirtyForNode(nodeId), 0);
      setTimeout(() => scheduleDirtyForNode(nodeId), 50);
    }
  };

  const handleTransformStart = (nodeId: string, _e: Konva.KonvaEventObject<Event>) => {
    // Check write permission first
    if (!checkWritePermission()) {
      // Detach transformer if any and bail
      try {
        transformerRef.current?.nodes([]);
        transformerRef.current?.getLayer()?.batchDraw();
      } catch { }
      return;
    }

    const nodeData = nodes.find((n) => n.id === nodeId) as any;
    const isLocked = !!(nodeData?.locked || nodeData?.attrs?.locked);
    if (isLocked) {
      // Detach transformer if any and bail
      try {
        transformerRef.current?.nodes([]);
        transformerRef.current?.getLayer()?.batchDraw();
      } catch { }
      return;
    }
    setIsTransforming(true);
    // Store initial state for all selected nodes when transform starts
    if (selectedIds.has(nodeId)) {
      selectedIds.forEach((id) => {
        const nodeData = nodes.find((n) => n.id === id);
        if (nodeData) {
          transformStartState.current.set(id, {
            x: nodeData.x,
            y: nodeData.y,
            w: nodeData.w ?? nodeData.width ?? 60,
            h: nodeData.h ?? nodeData.height ?? 60,
          });
        }
      });
    } else {
      const nodeData = nodes.find((n) => n.id === nodeId);
      if (nodeData) {
        transformStartState.current.set(nodeId, {
          x: nodeData.x,
          y: nodeData.y,
          w: nodeData.w ?? nodeData.width ?? 60,
          h: nodeData.h ?? nodeData.height ?? 60,
        });
      }
    }
    // Kick a targeted connectors recompute at transform start for visual sync
    scheduleDirtyForNode(nodeId);
  };

  const handleTransform = (nodeId: string, _e: Konva.KonvaEventObject<Event>) => {
    const nodeData = nodes.find((n) => n.id === nodeId);
    if (!nodeData || !isLiveSizedOrgNodeType(nodeData.type)) return;

    if (selectedIds.size > 1 && selectedIds.has(nodeId)) {
      scheduleDirtyForNodes(selectedIds);
      return;
    }

    scheduleDirtyForNode(nodeId);
  };

  return { handleTransformStart, handleTransform, handleTransformEnd };
}
